/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FunctionComponent } from 'react';
import type { SchoolImplementationPlanPostBody } from '@whizz/api-model';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import React, { useMemo, useState } from 'react';
import { useIdentity } from '@whizz/react-identity';
import { useI18n } from '@whizz/react-i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import { Alert, Button, Loader } from '../../components';
import { ImplementationActionSteps } from '../../components/implementation-plan/ImplementationActionSteps';
import { ImplementationPlanEditor } from '../../components/implementation-plan/ImplementationPlanEditor';
import { EmailPlanModal } from '../../components/implementation-plan/EmailPlanModal';
import CollapseSection from '../Home/modules/CollapseSection';
import { TermDatesEditor } from '../../components/term-dates/TermDatesEditor';
import { ImpactReportScheduleFields } from '../../components/impact-report-schedule/ImpactReportScheduleFields';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../util/vars';
import { useModal } from '../../hooks/useModal';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import useYearGroups from '../../hooks/useYearGroups';
import GlobalDashboardLayout from '../../layouts/GlobalDashboardLayout';

const ImplementationPlan: FunctionComponent = () => {
  const [ visionOpen, setVisionOpen ] = useState(true);
  const [ termDatesOpen, setTermDatesOpen ] = useState(false);
  const [ scheduleOpen, setScheduleOpen ] = useState(false);
  const { me } = useIdentity();
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();
  const readOnly = !(me as Teacher).getIsAdmin();
  const schoolId = (me as Teacher).getSchoolId()!;
  const schoolInfo = useSchoolInfo(schoolId);
  // Term dates and the report schedule only appear once scheduled impact reports are switched on for the school.
  const scheduleEnabled = !readOnly && !!schoolInfo.data?.impactReportScheduleEnabled;

  const planQuery = useQuery({
    queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_PLAN, schoolId ],
    queryFn: () => repository.SchoolImplementationPlanSelfRepository.get(schoolId),
    refetchInterval: (plan) => [ 'pending', 'running' ].includes(plan?.visionGeneration?.status ?? '') ? 5000 : false,
  });

  const guidanceQuery = useQuery({
    queryKey: [ 'implementation-guidance', schoolId ],
    queryFn: () => repository.SchoolImplementationPlanSelfRepository.genericGuidance(schoolId),
  });

  const defaultGoalsQuery = useQuery({
    queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_DEFAULT_GOALS, schoolId ],
    queryFn: () => repository.SchoolImplementationPlanSelfRepository.defaultGoals(schoolId),
  });

  const [ yearGroupsQuery ] = useYearGroups(schoolId);

  const classesQuery = useQuery({
    queryKey: [ QUERIES_IDS.CLASSES, schoolId ],
    queryFn: () => repository.NewClassRepository.getClasses(),
  });

  const tagsQuery = useQuery({
    queryKey: [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS, schoolId ],
    queryFn: () => repository.TagsRepository.getTags(),
  });

  const scheduleQuery = useQuery({
    queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_SCHEDULE, schoolId ],
    queryFn: () => repository.SchoolImpactReportScheduleRepository.getSchedule(schoolId),
    enabled: scheduleEnabled,
  });

  const termDatesQuery = useQuery({
    queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_TERM_DATES, schoolId ],
    queryFn: () => repository.SchoolImpactReportScheduleRepository.getTermDates(schoolId),
    enabled: scheduleEnabled,
  });

  const recipientsQueryKey = [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_REPORT_RECIPIENTS, schoolId ];

  const recipientsQuery = useQuery({
    queryKey: recipientsQueryKey,
    queryFn: () => repository.SchoolImpactReportScheduleRepository.getRecipients(schoolId),
    enabled: scheduleEnabled,
  });

  const staffQuery = useQuery({
    queryKey: [ QUERIES_IDS.STAFF ],
    queryFn: () => repository.StaffRepository.getStaff(),
    enabled: scheduleEnabled,
  });

  const recipientOptions = useMemo(() => (staffQuery.data?.teachers ?? []).map((teacher) => ({
    label: `${teacher.firstName} ${teacher.lastName ?? ''}`.trim(),
    value: teacher.id,
  })), [ staffQuery.data ]);

  const academicYear = termDatesQuery.data?.academicYear;
  const requireAcademicYear = (): number => {
    if (undefined === academicYear) throw new Error('Term dates are not loaded');
    return academicYear;
  };

  const invalidateSchedule = () => {
    queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_SCHEDULE ], exact: false });
  };

  const invalidateTermDates = () => {
    queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_TERM_DATES ], exact: false });
    // The schedule's upcoming-occurrences list is derived server-side from term dates, so it also
    // goes stale whenever a period is added, removed, or its dates change.
    invalidateSchedule();
  };

  const savePlanMutation = useMutation(
    (values: SchoolImplementationPlanPostBody) =>
      repository.SchoolImplementationPlanSelfRepository.save(schoolId, values),
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.IMPLEMENTATION_PLAN.GET_PLAN ], exact: false }),
    },
  );

  const saveScheduleMutation = useMutation(
    (patch: Parameters<typeof repository.SchoolImpactReportScheduleRepository.saveSchedule>[1]) =>
      repository.SchoolImpactReportScheduleRepository.saveSchedule(schoolId, patch),
    { onSuccess: invalidateSchedule },
  );

  const addOneOffMutation = useMutation(
    ({ date, note }: { date: string; note?: string }) =>
      repository.SchoolImpactReportScheduleRepository.addOneOffDate(schoolId, { date, note }),
    { onSuccess: invalidateSchedule },
  );

  const deleteOneOffMutation = useMutation(
    (id: number) => repository.SchoolImpactReportScheduleRepository.deleteOneOffDate(schoolId, id),
    { onSuccess: invalidateSchedule },
  );

  const toggleSkipMutation = useMutation(
    ({ occurrenceKey, skipped }: { occurrenceKey: string; skipped: boolean }) =>
      repository.SchoolImpactReportScheduleRepository.setOccurrenceSkipped(schoolId, occurrenceKey, { academicYear: requireAcademicYear(), skipped }),
    { onSuccess: invalidateSchedule },
  );

  const saveRecipientsMutation = useMutation(
    (teacherIds: number[]) => repository.SchoolImpactReportScheduleRepository.saveRecipients(schoolId, { teacherIds }),
    {
      onMutate: (teacherIds) => {
        queryClient.setQueryData(recipientsQueryKey, { teacherIds });
      },
      onSuccess: (recipients) => {
        queryClient.setQueryData(recipientsQueryKey, recipients);
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: recipientsQueryKey });
      },
    },
  );

  const saveTermDatesMutation = useMutation(
    (overrides: Parameters<typeof repository.SchoolImpactReportScheduleRepository.saveTermDates>[1]['overrides']) =>
      repository.SchoolImpactReportScheduleRepository.saveTermDates(schoolId, { academicYear: requireAcademicYear(), overrides }),
    { onSuccess: invalidateTermDates },
  );

  const resetTermDateMutation = useMutation(
    (termLabel: string) =>
      repository.SchoolImpactReportScheduleRepository.saveTermDates(schoolId, {
        academicYear: requireAcademicYear(),
        overrides: [ { termLabel, startDate: null, endDate: null } ],
      }),
    { onSuccess: invalidateTermDates },
  );

  const visionDrafts = useMemo(() => ({
    create: () => repository.SchoolImplementationPlanSelfRepository.createVisionDraft(schoolId),
    get: (id: number) => repository.SchoolImplementationPlanSelfRepository.getVisionDraft(schoolId, id),
    cancel: (id: number) => repository.SchoolImplementationPlanSelfRepository.cancelVisionDraft(schoolId, id),
  }), [ schoolId ]);

  // A stable component (not an inline wrapper), so the modal keeps its state when the page re-renders.
  const [ emailModal, toggleEmailModal ] = useModal(EmailPlanModal, { title: t('pages.IMPLEMENTATION_PLAN.EMAIL.TITLE'), disableMaskClick: true, schoolId });

  const isLoading = planQuery.isLoading || defaultGoalsQuery.isLoading || guidanceQuery.isLoading;
  const plan = planQuery.data;
  const hasPlanContent = !!plan?.vision?.trim() || !!plan?.goals?.length;
  const schedule = scheduleQuery.data;
  const termDates = termDatesQuery.data;
  const canHaveImpactReport = plan?.goals?.length;

  return (
    <GlobalDashboardLayout title={t('global.ROUTES.IMPLEMENTATION_PLAN')} fullWidthContent>
      {isLoading ? <Loader />
        : planQuery.isError || guidanceQuery.isError ? (
          <div role={'alert'}>
            <p>{t('pages.IMPLEMENTATION_ACTION_STEPS.ERROR')}</p>
            <button type={'button'} onClick={() => { planQuery.refetch(); guidanceQuery.refetch(); }}>{t('pages.IMPLEMENTATION_ACTION_STEPS.RETRY')}</button>
          </div>
        ) :
          <div className={'w-full max-w-7xl mx-auto flex flex-col gap-4'}>
            {emailModal}
            {readOnly ? null : (
              <div className={'container w-full pt-0 flex justify-end'}>
                <Button
                  type={'button'}
                  isOutline
                  icon={GLOBAL_ICONS.message}
                  disabled={!hasPlanContent || savePlanMutation.isLoading}
                  onClick={() => toggleEmailModal(true)}
                >
                  {t('pages.IMPLEMENTATION_PLAN.EMAIL.BUTTON')}
                </Button>
              </div>
            )}
            <CollapseSection
              autoScroll={false}
              headline={t('pages.IMPLEMENTATION_PLAN.VISION_AND_GOALS')}
              dataCy={'implementation_vision_goals'}
              open={visionOpen}
              setOpen={() => setVisionOpen((value) => !value)}
            >
              <div className={'flex flex-col gap-5 py-md'}>
                {!plan?.goals?.length && (
                  <Alert show type={'info'} disableIcon={false}>
                    {t(scheduleEnabled ? 'pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.EMPTY_STATE_HINT' : 'pages.IMPLEMENTATION_PLAN.EMPTY_STATE_HINT')}
                  </Alert>
                )}
                {savePlanMutation.isError && <p role={'alert'}>{t('pages.IMPLEMENTATION_ACTION_STEPS.SAVE_ERROR')}</p>}
                <ImplementationPlanEditor
                  readOnly={readOnly}
                  autoGenerateVision={plan?.autoGenerateVision}
                  visionGeneration={plan?.visionGeneration}
                  visionAuthorship={plan?.visionAuthorship}
                  drafts={visionDrafts}
                  vision={plan?.vision ?? ''}
                  goals={plan?.goals ?? []}
                  defaultGoals={defaultGoalsQuery.data ?? []}
                  yearGroups={(yearGroupsQuery.data ?? []).map((yg) => ({ id: yg.yearGroupId, name: yg.name }))}
                  schoolClasses={(classesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
                  schoolTags={(tagsQuery.data ?? []).map((tag) => ({ id: tag.id, abbreviation: tag.abbreviation }))}
                  onSave={(values) => savePlanMutation.mutateAsync(values)}
                />
              </div>
            </CollapseSection>
            <ImplementationActionSteps
              genericGuidance={guidanceQuery.data ?? []}
              goals={plan?.goals ?? []}
              cohorts={{
                yearGroup: (yearGroupsQuery.data ?? []).map((yg) => ({ id: yg.yearGroupId, name: yg.name })),
                class: (classesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name })),
                tag: (tagsQuery.data ?? []).map((tag) => ({ id: tag.id, name: tag.abbreviation })),
              }}
            />
            {scheduleEnabled && (
              <>
                {!canHaveImpactReport ? (
                  <Alert show type={'info'} disableIcon={false}>
                    {t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.NEEDS_IMPLEMENTATION_PLAN')}
                  </Alert>
                ) : null}
                <CollapseSection autoScroll={false} headline={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.TITLE')} dataCy={'implementation_term_dates'} open={termDatesOpen} setOpen={() => setTermDatesOpen((value) => !value)}>
                  <div className={'space-y-2'}>
                    <p className={'text-sm text-gray-500'}>{t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.SUBTITLE')}</p>
                    {termDatesQuery.isLoading && <Loader />}
                    {termDatesQuery.isError && (
                      <div role={'alert'}>
                        <p>{t('pages.COUNTRY_TERM_DATES.ERROR')}</p>
                        <button type={'button'} onClick={() => termDatesQuery.refetch()}>{t('pages.IMPLEMENTATION_ACTION_STEPS.RETRY')}</button>
                      </div>
                    )}
                    {termDates?.calendar && (
                      <div className={'text-sm space-y-1'}>
                        <p>{t(`pages.COUNTRY_TERM_DATES.${termDates.calendar.isFallback ? 'FALLBACK' : 'published' === termDates.calendar.status ? 'PUBLISHED' : 'ESTIMATED'}`)}</p>
                        {termDates.calendar.sourceUrl && (
                          <a className={'underline'} href={termDates.calendar.sourceUrl} target={'_blank'} rel={'noopener noreferrer'}>{t('pages.COUNTRY_TERM_DATES.SOURCE')}</a>
                        )}
                        {termDates.calendar.reviewedAt && <p>{t('pages.COUNTRY_TERM_DATES.REVIEWED', { date: termDates.calendar.reviewedAt })}</p>}
                      </div>
                    )}
                    {termDates && (
                      <TermDatesEditor
                        readOnly={readOnly}
                        key={JSON.stringify(termDates.terms)}
                        terms={termDates.terms}
                        onSave={async (overrides) => { await saveTermDatesMutation.mutateAsync(overrides); }}
                        onResetToDefault={async (termLabel) => { await resetTermDateMutation.mutateAsync(termLabel); }}
                      />
                    )}
                  </div>
                </CollapseSection>
                <CollapseSection autoScroll={false} headline={t('pages.ADMIN.IMPACT_REPORTS.SCHEDULE.TITLE')} dataCy={'implementation_report_schedule'} open={scheduleOpen} setOpen={() => setScheduleOpen((value) => !value)}>
                  <div className={'space-y-2'}>
                    {scheduleQuery.isLoading && <Loader />}
                    {scheduleQuery.isError && (
                      <div role={'alert'}>
                        <p>{t('pages.COUNTRY_TERM_DATES.SCHEDULE_ERROR')}</p>
                        <button type={'button'} onClick={() => scheduleQuery.refetch()}>{t('pages.IMPLEMENTATION_ACTION_STEPS.RETRY')}</button>
                      </div>
                    )}
                    {schedule && undefined !== academicYear && (
                      <ImpactReportScheduleFields
                        readOnly={readOnly}
                        schedule={schedule}
                        onSaveSchedule={(patch) => saveScheduleMutation.mutate(patch)}
                        onAddOneOffDate={(date, note) => addOneOffMutation.mutate({ date, note })}
                        onDeleteOneOffDate={(id) => deleteOneOffMutation.mutate(id)}
                        onToggleSkip={(occurrenceKey, skipped) => toggleSkipMutation.mutate({ occurrenceKey, skipped })}
                        recipientOptions={recipientOptions}
                        recipientIds={recipientsQuery.data?.teacherIds}
                        onSaveRecipients={(teacherIds) => saveRecipientsMutation.mutate(teacherIds)}
                      />
                    )}
                  </div>
                </CollapseSection>
              </>
            )}
          </div>
      }
    </GlobalDashboardLayout>
  );
};

export default ImplementationPlan;
